import { AuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';
import { OrganizationInfo, getPlanLimits, getEffectivePlan } from './planConfig';

// Define user roles
export type UserRole = 'admin' | 'editor' | 'viewer';

// Define actions/permissions
export enum Permission {
  // User Management
  VIEW_USERS = 'view_users',
  CREATE_USERS = 'create_users',
  EDIT_USERS = 'edit_users',
  DELETE_USERS = 'delete_users',

  // Client Management
  VIEW_CLIENTS = 'view_clients',
  CREATE_CLIENTS = 'create_clients',
  EDIT_CLIENTS = 'edit_clients',
  DELETE_CLIENTS = 'delete_clients',

  // Project Management
  VIEW_PROJECTS = 'view_projects',
  CREATE_PROJECTS = 'create_projects',
  EDIT_PROJECTS = 'edit_projects',
  DELETE_PROJECTS = 'delete_projects',

  // Task Management
  VIEW_TASKS = 'view_tasks',
  CREATE_TASKS = 'create_tasks',
  EDIT_TASKS = 'edit_tasks',
  DELETE_TASKS = 'delete_tasks',
  ASSIGN_TASKS = 'assign_tasks',

  // Analytics & Reporting
  VIEW_ANALYTICS = 'view_analytics',
  VIEW_DETAILED_ANALYTICS = 'view_detailed_analytics',

  // Profile & Settings
  VIEW_PROFILE = 'view_profile',
  EDIT_PROFILE = 'edit_profile',

  // Dashboard
  VIEW_DASHBOARD = 'view_dashboard',

  // System Administration
  MANAGE_SYSTEM = 'manage_system',
  VIEW_LOGS = 'view_logs',
  MANAGE_SETTINGS = 'manage_settings',
}

// Define role-based permissions mapping
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    // Admin has all permissions
    Permission.VIEW_USERS,
    Permission.CREATE_USERS,
    Permission.EDIT_USERS,
    Permission.DELETE_USERS,
    Permission.VIEW_CLIENTS,
    Permission.CREATE_CLIENTS,
    Permission.EDIT_CLIENTS,
    Permission.DELETE_CLIENTS,
    Permission.VIEW_PROJECTS,
    Permission.CREATE_PROJECTS,
    Permission.EDIT_PROJECTS,
    Permission.DELETE_PROJECTS,
    Permission.VIEW_TASKS,
    Permission.CREATE_TASKS,
    Permission.EDIT_TASKS,
    Permission.DELETE_TASKS,
    Permission.ASSIGN_TASKS,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_DETAILED_ANALYTICS,
    Permission.VIEW_PROFILE,
    Permission.EDIT_PROFILE,
    Permission.VIEW_DASHBOARD,
    Permission.MANAGE_SYSTEM,
    Permission.VIEW_LOGS,
    Permission.MANAGE_SETTINGS,
  ],
  editor: [
    // Editor can manage content but not users or system
    Permission.VIEW_USERS, // Can view but not manage users
    Permission.VIEW_CLIENTS,
    Permission.CREATE_CLIENTS,
    Permission.EDIT_CLIENTS,
    Permission.DELETE_CLIENTS,
    Permission.VIEW_PROJECTS,
    Permission.CREATE_PROJECTS,
    Permission.EDIT_PROJECTS,
    Permission.DELETE_PROJECTS,
    Permission.VIEW_TASKS,
    Permission.CREATE_TASKS,
    Permission.EDIT_TASKS,
    Permission.DELETE_TASKS,
    Permission.ASSIGN_TASKS,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_PROFILE,
    Permission.EDIT_PROFILE,
    Permission.VIEW_DASHBOARD,
  ],
  viewer: [
    // Viewer can only view content — no create, edit, delete, or profile changes
    Permission.VIEW_CLIENTS,
    Permission.VIEW_PROJECTS,
    Permission.VIEW_TASKS,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_DASHBOARD,
  ],
};

// Permission checking functions
export const hasPermission = (user: AuthenticatedUser | null, permission: Permission): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return ROLE_PERMISSIONS[user.role]?.includes(permission) || false;
};

export const hasAnyPermission = (
  user: AuthenticatedUser | null,
  permissions: Permission[]
): boolean => {
  if (!user) return false;
  return permissions.some(permission => hasPermission(user, permission));
};

export const hasAllPermissions = (
  user: AuthenticatedUser | null,
  permissions: Permission[]
): boolean => {
  if (!user) return false;
  return permissions.every(permission => hasPermission(user, permission));
};

// Role checking functions
export const isAdmin = (user: AuthenticatedUser | null): boolean => {
  return user?.role === 'admin';
};

export const isEditor = (user: AuthenticatedUser | null): boolean => {
  return user?.role === 'editor';
};

export const isViewer = (user: AuthenticatedUser | null): boolean => {
  return user?.role === 'viewer';
};

export const isAtLeastEditor = (user: AuthenticatedUser | null): boolean => {
  return user?.role === 'admin' || user?.role === 'editor';
};

/** Admins may modify any project; editors only when owner or assigned. */
export const canModifyProject = (
  user: AuthenticatedUser | null,
  isProjectOwner: boolean,
  isUserAssigned: boolean
): boolean => {
  if (isAdmin(user)) return true;
  return isAtLeastEditor(user) && (isProjectOwner || isUserAssigned);
};

/** Admins may modify any task; editors only when owner or assigned. */
export const canModifyTask = (
  user: AuthenticatedUser | null,
  isOwnerOrAssigned: boolean
): boolean => {
  if (isAdmin(user)) return true;
  return isAtLeastEditor(user) && isOwnerOrAssigned;
};

/** True when the user may create, edit, or delete app content. */
export const canPerformWrites = (user: AuthenticatedUser | null): boolean => {
  return isAtLeastEditor(user);
};

export const isAtLeastViewer = (user: AuthenticatedUser | null): boolean => {
  return user?.role === 'admin' || user?.role === 'editor' || user?.role === 'viewer';
};

// Get user permissions
export const getUserPermissions = (user: AuthenticatedUser | null): Permission[] => {
  if (!user) return [];
  if (user.role === 'admin') return Object.values(Permission);
  return ROLE_PERMISSIONS[user.role] || [];
};

// Check if user can access a specific tab/page
export const canAccessTab = (
  user: AuthenticatedUser | null,
  tab: string,
  organization?: OrganizationInfo | null
): boolean => {
  if (isAdmin(user)) {
    return ['overview', 'tasks', 'projects', 'clients', 'analytics', 'users', 'profile', 'settings'].includes(tab);
  }

  const limits = organization
    ? getPlanLimits(organization.plan, organization.trialEndsAt)
    : getPlanLimits('free');

  switch (tab) {
    case 'overview':
      return hasPermission(user, Permission.VIEW_DASHBOARD);
    case 'tasks':
      return hasPermission(user, Permission.VIEW_TASKS);
    case 'projects':
      return hasPermission(user, Permission.VIEW_PROJECTS);
    case 'clients':
      return hasPermission(user, Permission.VIEW_CLIENTS);
    case 'analytics':
      return hasPermission(user, Permission.VIEW_ANALYTICS) && limits.analytics;
    case 'users':
      return hasPermission(user, Permission.VIEW_USERS) && isAdmin(user) && limits.roleManagement;
    case 'profile':
      return hasPermission(user, Permission.VIEW_PROFILE);
    case 'settings':
      return hasPermission(user, Permission.MANAGE_SETTINGS) && isAdmin(user);
    default:
      return false;
  }
};

// Get available tabs for user
export const getAvailableTabs = (
  user: AuthenticatedUser | null,
  organization?: OrganizationInfo | null
): string[] => {
  const availableTabs: string[] = [];

  if (canAccessTab(user, 'overview', organization)) availableTabs.push('overview');
  if (canAccessTab(user, 'tasks', organization)) availableTabs.push('tasks');
  if (canAccessTab(user, 'projects', organization)) availableTabs.push('projects');
  if (canAccessTab(user, 'clients', organization)) availableTabs.push('clients');
  if (canAccessTab(user, 'analytics', organization)) availableTabs.push('analytics');
  if (canAccessTab(user, 'users', organization)) availableTabs.push('users');
  if (canAccessTab(user, 'profile', organization)) availableTabs.push('profile');
  if (canAccessTab(user, 'settings', organization)) availableTabs.push('settings');

  return availableTabs;
};

export const canUseFileUploads = (
  organization?: OrganizationInfo | null,
  user?: AuthenticatedUser | null
): boolean => {
  if (isAdmin(user ?? null)) return true;
  if (!organization) return false;
  return getPlanLimits(organization.plan, organization.trialEndsAt).fileUploads;
};

export const getEffectiveOrganizationPlan = (organization?: OrganizationInfo | null) => {
  if (!organization) return 'free' as const;
  return getEffectivePlan(organization.plan, organization.trialEndsAt);
};
